import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems } from '../../components/layout/DashboardSidebar/sidebarItems';
import {
  dashboardService,
  AppointmentWithDetails,
} from '../../services/dashboard/dashboardService';
import {
  videoConferenceService,
  ClinicalDataResponse,
  VideoCallChatMessage,
  CanJoinCallResponse,
} from '../../services/video-conference/videoConferenceService';
import { getConsultationTypeLabel } from '../../utils/appointmentUtils';
import { formatDateTime, formatDuration } from '../../utils/dateUtils';
import { getAppointmentStatusBadgeClass, getAppointmentStatusLabel } from '../../utils/statusUtils';
import { getInitials } from '../../utils/userUtils';
import './AppointmentDetailPage.css';

// Utility function to format markdown text for display
const formatChatMessage = (content: string): string => {
  if (!content) return '';
  
  // Remove "Appointment Summary Generated" prefix
  let formatted = content
    .replace(/Appointment Summary Generated\n\n/g, '')
    .replace(/Appointment Summary Generated/g, '');
  
  // Escape HTML first to prevent XSS
  formatted = formatted
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  // Convert markdown formatting to HTML
  // Convert **bold** to <strong>bold</strong> (non-greedy match)
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Convert line breaks to <br>
  formatted = formatted.replace(/\n/g, '<br>');
  
  return formatted;
};

const AppointmentDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [appointment, setAppointment] = useState<AppointmentWithDetails | null>(null);
  const [clinicalData, setClinicalData] = useState<ClinicalDataResponse | null>(null);
  const [chatMessages, setChatMessages] = useState<VideoCallChatMessage[]>([]);
  const [activeTab, setActiveTab] = useState<'summary' | 'chat'>('summary');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canJoinResponse, setCanJoinResponse] = useState<CanJoinCallResponse | null>(null);
  const [loadingCanJoin, setLoadingCanJoin] = useState(false);

  const loadAppointmentDetails = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);

      // Load appointment
      const apptData = await dashboardService.getAppointmentById(parseInt(id));
      setAppointment(apptData);

      // Load can-join status for scheduled/confirmed appointments
      if (apptData && (apptData.status === 'scheduled' || apptData.status === 'confirmed')) {
        try {
          setLoadingCanJoin(true);
          const joinStatus = await videoConferenceService.canJoinCallForAppointment(apptData.id);
          setCanJoinResponse(joinStatus);
        } catch (err) {
          console.warn('Could not check join status:', err);
          setCanJoinResponse({ can_join: false, message: 'Unable to check join status' });
        } finally {
          setLoadingCanJoin(false);
        }
      } else {
        setCanJoinResponse(null);
      }

      // Try to get video call request for this appointment
      try {
        const callRequestData = await videoConferenceService.getCallRequestForAppointment(parseInt(id));
        
        // If call request exists, load clinical data and chat messages
        if (callRequestData) {
          let hasClinicalData = false;
          try {
            const clinical = await videoConferenceService.getClinicalData(callRequestData.id);
            setClinicalData(clinical);
            hasClinicalData = true;
          } catch (err) {
            console.warn('No clinical data available for this appointment:', err);
            // Clinical data might not exist for all appointments
          }
          
          // Load chat messages for completed appointments
          if (apptData.status === 'completed') {
            try {
              const chatResponse = await videoConferenceService.getChatMessages(callRequestData.id);
              const messages = chatResponse.messages || [];
              setChatMessages(messages);
              
              // Set default tab: if no clinical data but there are chat messages, default to chat
              if (!hasClinicalData && messages.length > 0) {
                setActiveTab('chat');
              }
            } catch (err) {
              console.warn('No chat messages available for this appointment:', err);
              // Chat messages might not exist for all appointments
            }
          }
        }
      } catch (err) {
        console.warn('No video call request found for this appointment:', err);
        // Not all appointments have video calls
      }
    } catch (err: any) {
      console.error('Failed to load appointment details:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load appointment details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAppointmentDetails();
  }, [loadAppointmentDetails]);

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  const currentPath = location.pathname;
  const sidebarItems = createSidebarItems(navigate, currentPath);


  if (loading) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                Loading appointment details...
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div style={{ padding: '20px', background: '#fee2e2', borderRadius: '8px', color: '#b91c1c' }}>
                Error: {error || 'Appointment not found'}
              </div>
              <button
                onClick={() => navigate('/appointments')}
                style={{ marginTop: '20px', padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              >
                Back to Appointments
              </button>
            </main>
          </div>
        </div>
      </div>
    );
  }

  const patientName = appointment.patient_name || 'Unknown Patient';
  const initials = getInitials(patientName);

  return (
    <div className="dashboard">
      <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />

      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />

        <div className="dashboard-container">
          <main className="dashboard-main-content appointment-detail-content">
            <div className="appointment-detail-page-wrap">
              <div className="appointment-detail-one-card">
            <nav className="detail-breadcrumb" aria-label="Breadcrumb">
              <Link to="/appointments" className="breadcrumb-link">Appointments</Link>
              <span className="breadcrumb-sep" aria-hidden>›</span>
              <span className="breadcrumb-current">Appointment Details</span>
            </nav>
            <div className="appointment-detail-section">
              <h1 className="appointment-detail-title">Appointment Details</h1>
            </div>

            {/* Appointment Info */}
            <div className="appointment-detail-section">
            <div className="appointment-info-card">
              <div className="appointment-info-header">
                <div className="patient-info-header">
                  <div className="patient-avatar-large">{initials}</div>
                  <div>
                    <h2 className="patient-name-large">{patientName}</h2>
                    <div className="appointment-meta">
                      <span className={`status-badge ${getAppointmentStatusBadgeClass(appointment.status)}`}>
                        {getAppointmentStatusLabel(appointment.status)}
                      </span>
                      {appointment.appointment_type && (
                        <span className="appointment-type-badge">
                          {getConsultationTypeLabel(appointment.appointment_type)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="appointment-info-details">
                <div className="info-row">
                  <span className="info-label">Date & Time:</span>
                  <span className="info-value">{formatDateTime(appointment.appointment_date, true, 'long')}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Duration:</span>
                  <span className="info-value">{formatDuration(appointment.duration_minutes ?? appointment.duration ?? 30, 'minutes')}</span>
                </div>
                {appointment.patient_email && (
                  <div className="info-row">
                    <span className="info-label">Patient Email:</span>
                    <span className="info-value">{appointment.patient_email}</span>
                  </div>
                )}
                {appointment.notes && (
                  <div className="info-row">
                    <span className="info-label">Notes:</span>
                    <span className="info-value">{appointment.notes}</span>
                  </div>
                )}
              </div>

              <div className="appointment-info-card-actions">
                <button
                  type="button"
                  className="appointment-detail-btn appointment-detail-btn-edit"
                  onClick={() => navigate(`/appointments/${id}/edit`)}
                >
                  Edit
                </button>
                {(appointment.status === 'scheduled' || appointment.status === 'confirmed') && (
                  <>
                    <button
                      type="button"
                      className={`appointment-detail-btn appointment-detail-btn-join ${!canJoinResponse?.can_join ? 'disabled' : ''}`}
                      onClick={() => canJoinResponse?.can_join && id && navigate(`/video-conference/${id}`)}
                      disabled={!canJoinResponse?.can_join || loadingCanJoin}
                      title={!canJoinResponse?.can_join ? canJoinResponse?.message : 'Join video call'}
                    >
                      {loadingCanJoin ? 'Checking...' : 'Join Video Call'}
                    </button>
                    {!loadingCanJoin && !canJoinResponse?.can_join && canJoinResponse?.message && (
                      <span className="appointment-detail-join-note">{canJoinResponse.message}</span>
                    )}
                  </>
                )}
              </div>
            </div>
            </div>

            {/* Clinical Data and Chat Section - Only show for completed appointments */}
            {appointment.status === 'completed' && (clinicalData || chatMessages.length > 0) && (
              <div className="appointment-detail-section">
              <div className="clinical-data-section">
                {/* Tab Navigation */}
                <div className="appointment-tabs">
                  <button
                    className={`appointment-tab ${activeTab === 'summary' ? 'active' : ''}`}
                    onClick={() => setActiveTab('summary')}
                    disabled={!clinicalData}
                  >
                    Summary
                  </button>
                  <button
                    className={`appointment-tab ${activeTab === 'chat' ? 'active' : ''}`}
                    onClick={() => setActiveTab('chat')}
                    disabled={chatMessages.length === 0}
                  >
                    Chat {chatMessages.length > 0 && `(${chatMessages.length})`}
                  </button>
                </div>

                {/* Summary Tab Content */}
                {activeTab === 'summary' && clinicalData && (
                  <>
                <h2 className="section-title">Consultation Summary</h2>

                {/* Summary */}
                {clinicalData.summary && (
                  <div className="clinical-card">
                    <h3 className="clinical-card-title">Summary</h3>
                    <div className="clinical-card-content">
                      <p>{clinicalData.summary}</p>
                      {clinicalData.summary_edited && (
                        <div className="edited-badge">
                          Edited {clinicalData.summary_edited_at && new Date(clinicalData.summary_edited_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Prescriptions */}
                {clinicalData.prescriptions && clinicalData.prescriptions.length > 0 && (
                  <div className="clinical-card">
                    <h3 className="clinical-card-title">Prescriptions</h3>
                    <div className="clinical-card-content">
                      <div className="prescriptions-list">
                        {clinicalData.prescriptions.map((prescription) => (
                          <div key={prescription.id} className="prescription-item">
                            <div className="prescription-header">
                              <strong>{prescription.medication_name}</strong>
                              {prescription.dosage && <span className="prescription-detail">Dosage: {prescription.dosage}</span>}
                            </div>
                            {prescription.frequency && (
                              <div className="prescription-detail">Frequency: {prescription.frequency}</div>
                            )}
                            {prescription.duration && (
                              <div className="prescription-detail">Duration: {prescription.duration}</div>
                            )}
                            {prescription.instructions && (
                              <div className="prescription-instructions">{prescription.instructions}</div>
                            )}
                            {prescription.prescribed_at && (
                              <div className="prescription-meta">
                                Prescribed on {new Date(prescription.prescribed_at).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Lab Tests */}
                {clinicalData.lab_tests && clinicalData.lab_tests.length > 0 && (
                  <div className="clinical-card">
                    <h3 className="clinical-card-title">Lab Tests</h3>
                    <div className="clinical-card-content">
                      <div className="lab-tests-list">
                        {clinicalData.lab_tests.map((test) => (
                          <div key={test.id} className="lab-test-item">
                            <div className="lab-test-header">
                              <strong>{test.test_name}</strong>
                              {test.priority && (
                                <span className={`priority-badge priority-${test.priority.toLowerCase()}`}>
                                  {test.priority}
                                </span>
                              )}
                            </div>
                            {test.test_category && (
                              <div className="lab-test-detail">Category: {test.test_category}</div>
                            )}
                            {test.reason && (
                              <div className="lab-test-detail">Reason: {test.reason}</div>
                            )}
                            {test.instructions && (
                              <div className="lab-test-instructions">{test.instructions}</div>
                            )}
                            {test.notes && (
                              <div className="lab-test-notes">{test.notes}</div>
                            )}
                            {test.ordered_at && (
                              <div className="lab-test-meta">
                                Ordered on {new Date(test.ordered_at).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {!clinicalData.summary && 
                 (!clinicalData.prescriptions || clinicalData.prescriptions.length === 0) && 
                 (!clinicalData.lab_tests || clinicalData.lab_tests.length === 0) && (
                  <div className="no-clinical-data">
                    No clinical data available for this appointment.
                  </div>
                )}
                  </>
                )}

                {/* Chat Tab Content */}
                {activeTab === 'chat' && chatMessages.length > 0 && (
                  <>
                    <h2 className="section-title">Chat History</h2>
                    <div className="clinical-card">
                      <div className="clinical-card-content">
                        <div className="chat-messages-list">
                          {chatMessages.map((message) => {
                            const isDoctorMessage = message.sender_type === 'doctor';
                            const isAIAgentMessage = message.sender_type === 'ai_agent';
                            
                            return (
                              <div
                                key={message.id}
                                className={`chat-message-item ${isDoctorMessage ? 'doctor' : isAIAgentMessage ? 'ai-agent' : 'patient'}`}
                              >
                                <div className="chat-message-header">
                                  <span className="chat-message-sender">
                                    {isDoctorMessage ? '👨‍⚕️ Doctor' : isAIAgentMessage ? '🤖 AI Agent' : '👤 Patient'}
                                  </span>
                                  {message.created_at && (
                                    <span className="chat-message-time">
                                      {new Date(message.created_at).toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </span>
                                  )}
                                </div>
                                <div 
                                  className="chat-message-text"
                                  dangerouslySetInnerHTML={{ __html: formatChatMessage(message.content) }}
                                />
                                {isDoctorMessage && message.recipient_type && (
                                  <div className="chat-message-recipient">
                                    → {message.recipient_type === 'ai_agent' ? 'AI Agent' : 'Patient'}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* No data message */}
                {activeTab === 'summary' && !clinicalData && (
                  <div className="no-clinical-data">
                    No clinical data available for this appointment.
                  </div>
                )}
                {activeTab === 'chat' && chatMessages.length === 0 && (
                  <div className="no-clinical-data">
                    No chat messages available for this appointment.
                  </div>
                )}
              </div>
              </div>
            )}

            {appointment.status === 'completed' && !clinicalData && chatMessages.length === 0 && (
              <div className="appointment-detail-section">
                <div className="clinical-data-section">
                  <h2 className="section-title">Consultation Summary</h2>
                  <div className="no-clinical-data">
                    No clinical data or chat messages available for this appointment.
                  </div>
                </div>
              </div>
            )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AppointmentDetailPage;
